"use client";

import { EvmHolding, SolanaHolding } from '@/types';

function getChainName(holding: EvmHolding | SolanaHolding): string {
  if ('chainId' in holding) {
    // EVM chains
    switch (holding.chainId) {
      case 8453:
        return 'Base';
      case 1:
        return 'Ethereum';
      case 1151111081099710:
        return 'Solana';
      default:
        return `Chain ${holding.chainId}`;
    }
  }
  // Solana holdings
  return 'Solana';
}

interface HoldingsProps {
  ethereum: EvmHolding[];
  solana: SolanaHolding[];
}

const MIN_USD_VALUE = 0.01;

export function Holdings({ ethereum, solana }: HoldingsProps) {
  if (ethereum.length === 0 && solana.length === 0) {
    return (
      <div className="bg-white shadow sm:rounded-lg p-8 text-center">
        <div className="text-gray-600">
          No holdings found. Deposit funds into your Base or Solana address to get started.
        </div>
      </div>
    );
  }

  // Combine, filter and sort all holdings by USD value
  const allHoldings = [...ethereum, ...solana]
    .filter(holding => (holding.usd_value || 0) >= MIN_USD_VALUE)
    .sort((a, b) => {
      const aValue = a.usd_value || 0;
      const bValue = b.usd_value || 0;
      return bValue - aValue;
    });

  return (
    <div className="bg-white shadow sm:rounded-lg overflow-hidden">
      <ul role="list" className="divide-y divide-gray-200">
        {allHoldings.map((holding) => (
          <li 
            key={`${holding.address}-${holding.assetClass}`}
            className="hover:bg-gray-50 transition-colors duration-150 ease-in-out"
          >
            <div className="px-4 py-4 sm:px-6">
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  {holding.thumbnail ? (
                    <img 
                      src={holding.thumbnail} 
                      alt={holding.symbol}
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white font-bold">
                      {holding.symbol.slice(0, 2)}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {holding.name || holding.symbol}
                    </p>
                    {holding.type !== 'native' && holding.address && (
                      <a 
                        href={`https://dexscreener.com/${getChainName(holding).toLowerCase()}/${holding.address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-gray-400 font-mono hover:text-blue-500 transition-colors break-all"
                      >
                        {holding.address}
                      </a>
                    )}
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {getChainName(holding)}
                    </span>
                  </div>
                  <div className="mt-1">
                    
                    <p className="text-sm text-gray-600">
                      {parseFloat(holding.balance).toLocaleString(undefined, { 
                        maximumFractionDigits: 6 
                      })} {holding.symbol}
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {holding.usd_value && (
                    <div className="text-sm text-gray-600">
                      ${holding.usd_value.toLocaleString(undefined, { 
                        maximumFractionDigits: 2 
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
} 